import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { batchDeleteElement } from '@/app/apis';
import { toast } from '@/app/components/ui/use-toast';
import { useStore } from '@/app/providers/store';
import type { ElementType } from '@/app/types';
import { useElementErrorMsg } from './use-element-error-msg';
import { useFetch } from './use-fetch';
import { useGlobalModal } from './use-global-modal';

interface UseDeleteElementsOptions {
  /** 删除成功后的回调 */
  onSuccess?: (deletedIds: string[]) => void;
}

/**
 * 主体元素删除 Hook
 * 处理：
 * 1. 单个/批量删除
 * 2. 根据主体元素状态显示不同的确认提示
 * 3. 删除成功后更新状态
 */
export function useDeleteElements(options?: UseDeleteElementsOptions) {
  const { onSuccess } = options || {};
  const t = useTranslations('elements');

  const { showErrorMsg, showSuccessMsg } = useElementErrorMsg();
  const { open: openConfirmDialog, close: closeConfirmDialog } = useGlobalModal('confirmDialog');
  const { updateReferenceModalUpdateReason } = useStore((state) => ({
    updateReferenceModalUpdateReason: state.updateReferenceModalUpdateReason,
  }));

  const { run: deleteElements, loading } = useFetch(
    (deleteIds: string[]) =>
      batchDeleteElement({
        body: {
          ids: deleteIds,
        },
      }),
    {
      manual: true,
      onSuccess: (data, params) => {
        if (data.error) {
          showErrorMsg(data.error);
        } else {
          showSuccessMsg();
          updateReferenceModalUpdateReason({
            type: 'delete',
            ids: params[0],
          });
          onSuccess?.(params[0]);
          closeConfirmDialog();
        }
      },
    },
  );

  /**
   * 执行删除操作
   * @param elements 要删除的元素数组
   */
  const handleDelete = useCallback(
    (elements: ElementType | ElementType[]) => {
      const elementsArray = Array.isArray(elements) ? elements : [elements];
      const deleteIds = elementsArray.map((el) => el.id || '').filter(Boolean);

      if (!deleteIds.length) return;

      if (deleteIds.length > 10) {
        toast({
          type: 'error',
          message: t('elementDialog.maxDeleteLimitTip', {
            n: 10,
          }),
        });
        return;
      }

      // 检查是否有已审核通过的共享主体
      const hasApprovedShareElement = elementsArray.some((el) => el.share_meta?.status === 'approved');

      if (hasApprovedShareElement) {
        openConfirmDialog({
          content: t('deleteShareReference.title'),
          secondaryButtonText: t('deleteShareReference.leftBtn'),
          primaryButtonText: t('deleteShareReference.rightBtn'),
          onPrimaryAction: () => {
            deleteElements(deleteIds);
          },
        });
      } else {
        openConfirmDialog({
          content: t('deleteElement.title'),
          secondaryButtonText: t('deleteElement.leftBtn'),
          primaryButtonText: t('deleteElement.rightBtn'),
          onPrimaryAction: () => {
            deleteElements(deleteIds);
          },
        });
      }
    },
    [deleteElements, openConfirmDialog],
  );

  return {
    handleDelete,
    loading,
  };
}
